import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * A button.
 *
 * Authored as a native Custom Element, so the same tag works unmodified in any
 * stack that can print HTML: WordPress, Drupal, Laravel, Astro, Nuxt, a plain
 * page. Styles reference the semantic token layer only; a reference to a
 * primitive (--v-ref-*) would reach past that layer and is rejected by
 * `npm run lint`.
 *
 * @example <v-button variant="primary" size="md">Save</v-button>
 */
@customElement('v-button')
export class VButton extends LitElement {
  static override styles = css`
    :host {
      display: inline-block;
    }

    button {
      font-family: var(--v-font-family-body);
      line-height: var(--v-type-body-medium-line-height);
      border-style: solid;
      border-width: var(--v-control-border-width);
      border-color: transparent;
      border-radius: var(--v-control-radius);
      cursor: pointer;

      &:focus-visible {
        outline: var(--v-focus-ring-outset-outer-spread) solid var(--v-colors-focus-ring);
        outline-offset: var(--v-focus-ring-outset-inner-spread);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }

    :host([size='sm']) button {
      font-size: var(--v-type-body-small-font-size);
      padding: var(--v-control-padding-block) var(--v-control-padding-inline);
    }

    :host([size='md']) button {
      font-size: var(--v-type-body-medium-font-size);
      padding: var(--v-control-padding-block) var(--v-control-padding-inline);
    }

    :host([size='lg']) button {
      font-size: var(--v-type-body-large-font-size);
      padding: var(--v-control-padding-inline) var(--v-surface-padding);
    }

    :host([variant='primary']) button {
      background-color: var(--v-colors-brand-primary);
      color: var(--v-colors-brand-tertiary);
    }

    :host([variant='secondary']) button {
      background-color: var(--v-colors-surface-primary);
      color: var(--v-colors-text-primary);
      border-color: var(--v-colors-borders);

      &:hover:not(:disabled) {
        background-color: var(--v-colors-surface-secondary);
      }
    }

    :host([variant='danger']) button {
      background-color: var(--v-colors-status-error-background);
      color: var(--v-colors-status-error-text);
      border-color: var(--v-colors-status-error-border);
    }
  `;


  /** Visual role of the button. */
  @property({ reflect: true }) variant: ButtonVariant = 'primary';

  /** Control size. */
  @property({ reflect: true }) size: ButtonSize = 'md';

  /** Disables the inner button. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Forwarded to the inner button, for use inside a form. */
  @property() type: 'button' | 'submit' | 'reset' = 'button';

  override render() {
    return html`
      <button part="button" type=${this.type} ?disabled=${this.disabled}>
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'v-button': VButton;
  }
}
